import React, { useState, useEffect, useCallback } from 'react';
import {
    Container,
    Grid,
    Box,
    Typography,
    TextField,
    InputAdornment,
    FormControl,
    Select,
    MenuItem,
    Slider,
    Chip,
    Button,
    Card,
    CardMedia,
    CardContent,
    IconButton,
    Pagination,
    Breadcrumbs,
    Link,
    Rating,
    Drawer,
    useMediaQuery,
    Divider,
    Fade,
    Zoom,
    Grow,
    Slide,
    Snackbar,
    Alert,
    Paper
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import CloseIcon from '@mui/icons-material/Close';
import DiamondIcon from '@mui/icons-material/Diamond';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { demoProducts } from '../data/demoData';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import './Shop.css';

// Styled Components with Premium Effects
const StyledCard = styled(Card)(({ theme }) => ({
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    overflow: 'hidden',
    transition: 'all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        border: '1px solid rgba(198, 164, 63, 0.3)',
        borderRadius: '12px',
        pointerEvents: 'none',
        transition: 'all 0.4s ease',
    },
    '&:hover': {
        transform: 'translateY(-12px) scale(1.02)',
        boxShadow: '0 30px 60px rgba(198, 164, 63, 0.2), 0 0 0 1px rgba(198, 164, 63, 0.5)',
        '&::before': {
            borderColor: '#C6A43F',
            boxShadow: '0 0 20px rgba(198, 164, 63, 0.3)',
        },
        '& .MuiCardMedia-root': {
            transform: 'scale(1.08)',
        },
        '& .product-actions': {
            opacity: 1,
            transform: 'translateY(0)',
        }
    },
}));

const StyledCardMedia = styled(CardMedia)({
    transition: 'transform 0.6s cubic-bezier(0.165, 0.84, 0.44, 1)',
    height: '320px',
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to bottom, rgba(0,0,0,0) 70%, rgba(0,0,0,0.1) 100%)',
        pointerEvents: 'none',
    }
});

const PriceSlider = styled(Slider)({
    color: '#C6A43F',
    height: 4,
    '& .MuiSlider-track': {
        border: 'none',
    },
    '& .MuiSlider-rail': {
        opacity: 0.3,
        backgroundColor: '#E5D5AC',
    },
    '& .MuiSlider-thumb': {
        height: 20,
        width: 20,
        backgroundColor: '#FFFFFF',
        border: '2px solid #C6A43F',
        '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
            boxShadow: '0 0 0 8px rgba(198, 164, 63, 0.16)',
        },
        '&::before': {
            display: 'none',
        },
    },
    '& .MuiSlider-valueLabel': {
        backgroundColor: '#C6A43F',
        color: '#0A0F1E',
        fontWeight: 600,
        fontSize: '12px',
    },
});

const StickyFilterPaper = styled(Paper)(({ theme }) => ({
    position: 'sticky',
    top: theme.spacing(3),
    maxHeight: `calc(100vh - ${theme.spacing(6)})`,
    overflowY: 'auto',
    backgroundColor: '#FFFFFF',
    borderRadius: '12px',
    border: '1px solid rgba(198, 164, 63, 0.2)',
    '&::-webkit-scrollbar': {
        width: '4px',
    },
    '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: '#C6A43F',
        borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
        background: '#B3923A',
    },
}));

const AnimatedBox = motion(Box);

const Shop = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { addToCart, addToWishlist, removeFromWishlist } = useCart();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    // const [products, setProducts] = useState(demoProducts);
    const products = demoProducts; // Using static demo data for now
    const [filteredProducts, setFilteredProducts] = useState(demoProducts);
    const [page, setPage] = useState(1);
    const [favorites, setFavorites] = useState([]);
    const [filters, setFilters] = useState({
        category: 'all',
        metalType: 'all',
        priceRange: [0, 500000],
        purity: 'all',
        sortBy: 'featured',
        searchQuery: ''
    });
    const [hoveredProduct, setHoveredProduct] = useState(null);
    // const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({ 
        open: false, 
        message: '', 
        severity: 'success',
        productId: null 
    });

    const productsPerPage = 9;
    const categories = ['all', 'gold', 'silver', 'diamond', 'platinum'];
    // const metalTypes = ['all', 'gold', 'silver', 'platinum'];
    const purities = {
        gold: ['all', '24k', '22k', '18k', '14k'],
        silver: ['all', '999', '925'],
        diamond: ['all', 'natural', 'labgrown']
    };

    const filterProducts = useCallback(() => {
        // setLoading(true);

        setTimeout(() => {
            let filtered = [...products];

            if (filters.category !== 'all') {
                filtered = filtered.filter(p => p.category === filters.category);
            }

            if (filters.metalType !== 'all') {
                filtered = filtered.filter(p => p.metalType === filters.metalType);
            }

            if (filters.purity !== 'all') {
                filtered = filtered.filter(p => p.metalPurity === filters.purity);
            }

            filtered = filtered.filter(p =>
                p.finalPrice >= filters.priceRange[0] &&
                p.finalPrice <= filters.priceRange[1]
            );

            if (filters.searchQuery) {
                filtered = filtered.filter(p =>
                    p.name.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
                    p.description.toLowerCase().includes(filters.searchQuery.toLowerCase())
                );
            }

            switch (filters.sortBy) {
                case 'price_low':
                    filtered.sort((a, b) => a.finalPrice - b.finalPrice);
                    break;
                case 'price_high':
                    filtered.sort((a, b) => b.finalPrice - a.finalPrice);
                    break;
                case 'newest':
                    filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
                    break;
                case 'rating':
                    filtered.sort((a, b) => b.rating - a.rating);
                    break;
                default:
                    break;
            }

            setFilteredProducts(filtered);
            setPage(1);
            // setLoading(false);
        }, 500);
    }, [filters, products]);

    useEffect(() => {
        filterProducts();
    }, [filters, products, filterProducts]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleAddToCart = (e, product) => {
        e.stopPropagation();
        addToCart(product, 1);
        setSnackbar({
            open: true,
            message: `✨ ${product.name} added to cart`,
            severity: 'success',
            productId: product.id
        });
    };

    const handleFavoriteToggle = (e, productId) => {
        e.stopPropagation();
        const product = products.find(p => p.id === productId);
        
        if (favorites.includes(productId)) {
            setFavorites(prev => prev.filter(id => id !== productId));
            removeFromWishlist(productId);
            setSnackbar({
                open: true,
                message: `♡ Removed from wishlist`,
                severity: 'info',
                productId
            });
        } else {
            setFavorites(prev => [...prev, productId]);
            addToWishlist(product);
            setSnackbar({
                open: true,
                message: `❤️ Added to wishlist`,
                severity: 'success',
                productId
            });
        }
    };

    const handleQuickBuy = (e, product) => {
        e.stopPropagation();
        addToCart(product, 1);
        navigate('/checkout');
    };

    const pageCount = Math.ceil(filteredProducts.length / productsPerPage);
    const displayedProducts = filteredProducts.slice(
        (page - 1) * productsPerPage,
        page * productsPerPage
    );

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const FilterContent = () => (
        <Box sx={{ p: 3 }}>
            <AnimatedBox
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5 }}
            >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{
                        fontFamily: 'Cormorant Garamond',
                        fontWeight: 700,
                        color: '#0A0F1E',
                        letterSpacing: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                    }}>
                        <TuneIcon sx={{ color: '#C6A43F' }} />
                        Filters
                    </Typography>
                    {isMobile && (
                        <IconButton onClick={() => setFilterDrawerOpen(false)} sx={{ color: '#C6A43F' }}>
                            <CloseIcon />
                        </IconButton>
                    )}
                </Box>

                <Divider sx={{ mb: 3, borderColor: 'rgba(198, 164, 63, 0.2)' }} />

                <Grow in={true} timeout={300}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: '#C6A43F',
                            letterSpacing: '1px',
                            fontSize: '0.9rem'
                        }}>
                            CATEGORY
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={filters.category}
                                onChange={(e) => handleFilterChange('category', e.target.value)}
                                sx={{
                                    bgcolor: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(198, 164, 63, 0.3)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#C6A43F',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#C6A43F',
                                    }
                                }}
                            >
                                {categories.map(cat => (
                                    <MenuItem key={cat} value={cat}>
                                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Grow>

                <Grow in={true} timeout={400}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: '#C6A43F',
                            letterSpacing: '1px',
                            fontSize: '0.9rem'
                        }}>
                            PRICE RANGE
                        </Typography>
                        <PriceSlider
                            value={filters.priceRange}
                            onChange={(e, newValue) => handleFilterChange('priceRange', newValue)}
                            valueLabelDisplay="auto"
                            min={0}
                            max={500000}
                            step={1000}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                            <Typography variant="body2" sx={{ color: '#666' }}>
                                ₹{filters.priceRange[0].toLocaleString()}
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#C6A43F', fontWeight: 600 }}>
                                ₹{filters.priceRange[1].toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                </Grow>

                <Grow in={true} timeout={500}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="subtitle1" sx={{
                            mb: 2,
                            fontWeight: 600,
                            color: '#C6A43F',
                            letterSpacing: '1px',
                            fontSize: '0.9rem'
                        }}>
                            METAL PURITY
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={filters.purity}
                                onChange={(e) => handleFilterChange('purity', e.target.value)}
                                sx={{
                                    bgcolor: 'white',
                                    '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(198, 164, 63, 0.3)',
                                    }
                                }}
                            >
                                {purities[filters.category === 'all' ? 'gold' : filters.category]?.map(p => (
                                    <MenuItem key={p} value={p}>
                                        {p === 'all' ? 'All' : p.toUpperCase()}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </Grow>

                <Zoom in={true} timeout={600}>
                    <Button
                        fullWidth
                        variant="outlined"
                        sx={{
                            mt: 2,
                            borderColor: '#C6A43F',
                            color: '#C6A43F',
                            '&:hover': {
                                borderColor: '#B3923A',
                                backgroundColor: 'rgba(198, 164, 63, 0.04)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 5px 15px rgba(198, 164, 63, 0.2)'
                            },
                            transition: 'all 0.3s ease'
                        }}
                        onClick={() => setFilters({
                            category: 'all',
                            metalType: 'all',
                            priceRange: [0, 500000],
                            purity: 'all',
                            sortBy: 'featured',
                            searchQuery: ''
                        })}
                    >
                        Reset All Filters
                    </Button>
                </Zoom>
            </AnimatedBox>
        </Box>
    );

    return (
        <Box className="shop-page">
            {/* Animated Hero Section */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1 }}
                className="shop-hero"
            >
                <Container maxWidth="lg">
                    <motion.div
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                    >
                        <Typography variant="h1" className="hero-title">
                            Our Collection
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    >
                        <Typography variant="subtitle1" className="hero-subtitle">
                            Crafted to be cherished, designed to last forever
                        </Typography>
                    </motion.div>

                    {/* Animated Search Bar */}
                    <motion.div
                        initial={{ y: 50, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        className="search-container"
                    >
                        <TextField
                            fullWidth
                            placeholder="Search for jewelry pieces..."
                            value={filters.searchQuery}
                            onChange={(e) => handleFilterChange('searchQuery', e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#C6A43F' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    bgcolor: 'white',
                                    borderRadius: '50px',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: 'rgba(198, 164, 63, 0.3)',
                                            borderRadius: '50px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#C6A43F',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#C6A43F',
                                            borderWidth: '2px',
                                        },
                                    },
                                }
                            }}
                        />
                    </motion.div>

                    {/* Floating Gold Particles Animation */}
                    <div className="floating-particles">
                        {[...Array(20)].map((_, i) => (
                            <motion.div
                                key={i}
                                className="particle"
                                animate={{
                                    y: [0, -30, 0],
                                    x: [0, i % 2 === 0 ? 20 : -20, 0],
                                    rotate: [0, 180, 360],
                                }}
                                transition={{
                                    duration: 3 + i % 3,
                                    repeat: Infinity,
                                    delay: i * 0.2,
                                }}
                            />
                        ))}
                    </div>
                </Container>
            </motion.div>

            <Container maxWidth="xl" sx={{ py: 6 }}>
                {/* Animated Breadcrumbs */}
                <Fade in={true} timeout={800}>
                    <Breadcrumbs sx={{ mb: 4 }}>
                        <Link
                            color="inherit"
                            href="/"
                            sx={{
                                textDecoration: 'none',
                                '&:hover': {
                                    color: '#C6A43F',
                                    textDecoration: 'underline'
                                }
                            }}
                        >
                            Home
                        </Link>
                        <Typography color="text.primary">Collection</Typography>
                    </Breadcrumbs>
                </Fade>

                {/* Mobile Filter Button with Animation */}
                {isMobile && (
                    <Zoom in={true} timeout={800}>
                        <Button
                            startIcon={<FilterListIcon />}
                            onClick={() => setFilterDrawerOpen(true)}
                            sx={{
                                mb: 3,
                                color: '#C6A43F',
                                borderColor: '#C6A43F',
                                '&:hover': {
                                    backgroundColor: 'rgba(198, 164, 63, 0.04)',
                                    transform: 'translateY(-2px)'
                                }
                            }}
                            variant="outlined"
                        >
                            Show Filters
                        </Button>
                    </Zoom>
                )}

                <Grid container spacing={4}>
                    {/* Desktop Filters - Now Sticky */}
                    {!isMobile && (
                        <Grid item md={3}>
                            <Slide direction="right" in={true} timeout={800}>
                                <div>
                                    <StickyFilterPaper elevation={0}>
                                        <FilterContent />
                                    </StickyFilterPaper>
                                </div>
                            </Slide>
                        </Grid>
                    )}

                    {/* Mobile Filters Drawer */}
                    <Drawer
                        anchor="left"
                        open={filterDrawerOpen}
                        onClose={() => setFilterDrawerOpen(false)}
                        PaperProps={{
                            sx: {
                                width: '90%',
                                maxWidth: 320,
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(10px)',
                            }
                        }}
                    >
                        <FilterContent />
                    </Drawer>

                    {/* Products Grid - Right Side */}
                    <Grid item xs={12} md={9}>
                        {/* Results Summary with Animation */}
                        <Fade item xs={3} in={true} timeout={1000}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3,
                                pb: 2,
                                borderBottom: '1px solid rgba(198, 164, 63, 0.2)'
                            }}>
                                <Typography variant="body2" sx={{ color: '#666' }}>
                                    Showing <span style={{ color: '#C6A43F', fontWeight: 600 }}>{displayedProducts.length}</span> of {filteredProducts.length} products
                                </Typography>
                                {!isMobile && (
                                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                        <Typography variant="body2" sx={{ color: '#666' }}>
                                            Sort by:
                                        </Typography>
                                        <FormControl size="small" sx={{ minWidth: 150 }}>
                                            <Select
                                                value={filters.sortBy}
                                                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                                sx={{
                                                    bgcolor: 'white',
                                                    '& .MuiOutlinedInput-notchedOutline': {
                                                        borderColor: 'rgba(198, 164, 63, 0.3)',
                                                    }
                                                }}
                                            >
                                                <MenuItem value="featured">Featured</MenuItem>
                                                <MenuItem value="newest">Newest First</MenuItem>
                                                <MenuItem value="price_low">Price: Low to High</MenuItem>
                                                <MenuItem value="price_high">Price: High to Low</MenuItem>
                                                <MenuItem value="rating">Highest Rated</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Box>
                                )}
                            </Box>
                        </Fade>

                        {/* Products Grid with Stagger Animation */}
                        <Grid item xs={12} gap={3}>
                            <AnimatePresence>
                                {displayedProducts.map((product, index) => (
                                    <Grid item xs={12} sm={12} lg={12} key={product.id}>
                                        <motion.div
                                            initial={{ opacity: 0, y: 50 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -50 }}
                                            transition={{
                                                duration: 0.5,
                                                delay: index * 0.1,
                                                type: "spring",
                                                stiffness: 100
                                            }}
                                            whileHover={{ scale: 1.02 }}
                                            onHoverStart={() => setHoveredProduct(product.id)}
                                            onHoverEnd={() => setHoveredProduct(null)}
                                            onClick={() => navigate(`/product/${product.id}`)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <StyledCard>
                                                <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                                                    <StyledCardMedia
                                                        component="img"
                                                        image={product.images?.[0] || '/images/placeholder.jpg'}
                                                        alt={product.name}
                                                    />

                                                    {/* Animated Badges */}
                                                    <AnimatePresence>
                                                        {product.isNew && (
                                                            <motion.div
                                                                initial={{ x: -100, opacity: 0 }}
                                                                animate={{ x: 0, opacity: 1 }}
                                                                exit={{ x: -100, opacity: 0 }}
                                                                transition={{ type: "spring", stiffness: 200 }}
                                                            >
                                                                <Chip
                                                                    label="NEW"
                                                                    size="small"
                                                                    sx={{
                                                                        position: 'absolute',
                                                                        top: 16,
                                                                        left: 16,
                                                                        bgcolor: '#C6A43F',
                                                                        color: '#0A0F1E',
                                                                        fontWeight: 700,
                                                                        letterSpacing: '1px',
                                                                        fontSize: '0.7rem',
                                                                        boxShadow: '0 4px 10px rgba(198, 164, 63, 0.3)'
                                                                    }}
                                                                />
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>

                                                    {/* Animated Favorite Button */}
                                                    <motion.div
                                                        whileHover={{ scale: 1.1 }}
                                                        whileTap={{ scale: 0.9 }}
                                                    >
                                                        <IconButton
                                                            sx={{
                                                                position: 'absolute',
                                                                top: 16,
                                                                right: 16,
                                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                                backdropFilter: 'blur(5px)',
                                                                '&:hover': {
                                                                    bgcolor: 'white',
                                                                    boxShadow: '0 4px 15px rgba(198, 164, 63, 0.2)'
                                                                },
                                                                transition: 'all 0.3s ease'
                                                            }}
                                                            onClick={(e) => handleFavoriteToggle(e, product.id)}
                                                        >
                                                            <motion.div
                                                                animate={{ scale: favorites.includes(product.id) ? [1, 1.2, 1] : 1 }}
                                                                transition={{ duration: 0.3 }}
                                                            >
                                                                {favorites.includes(product.id) ? (
                                                                    <FavoriteIcon sx={{ color: '#C6A43F' }} />
                                                                ) : (
                                                                    <FavoriteBorderIcon />
                                                                )}
                                                            </motion.div>
                                                        </IconButton>
                                                    </motion.div>

                                                    {/* Animated Quick Actions */}
                                                    <motion.div
                                                        className="product-actions"
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{
                                                            opacity: hoveredProduct === product.id ? 1 : 0,
                                                            y: hoveredProduct === product.id ? 0 : 20
                                                        }}
                                                        transition={{ duration: 0.3 }}
                                                        style={{
                                                            position: 'absolute',
                                                            bottom: 16,
                                                            left: 0,
                                                            right: 0,
                                                            display: 'flex',
                                                            justifyContent: 'center',
                                                            gap: 8,
                                                            zIndex: 10
                                                        }}
                                                    >
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<ShoppingBagIcon />}
                                                            size="small"
                                                            onClick={(e) => handleAddToCart(e, product)}
                                                            sx={{
                                                                bgcolor: '#0A0F1E',
                                                                '&:hover': {
                                                                    bgcolor: '#C6A43F',
                                                                    transform: 'translateY(-2px)',
                                                                    boxShadow: '0 5px 15px rgba(198, 164, 63, 0.3)'
                                                                },
                                                                transition: 'all 0.3s ease',
                                                                borderRadius: '25px',
                                                                px: 3
                                                            }}
                                                        >
                                                            Add to Cart
                                                        </Button>
                                                        <Button
                                                            variant="outlined"
                                                            size="small"
                                                            onClick={(e) => handleQuickBuy(e, product)}
                                                            sx={{
                                                                borderColor: '#C6A43F',
                                                                color: '#C6A43F',
                                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                                '&:hover': {
                                                                    borderColor: '#B3923A',
                                                                    bgcolor: 'white',
                                                                },
                                                                transition: 'all 0.3s ease',
                                                                borderRadius: '25px',
                                                                px: 2
                                                            }}
                                                        >
                                                            Buy Now
                                                        </Button>
                                                    </motion.div>
                                                </Box>

                                                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                                                    <motion.div
                                                        initial={false}
                                                        animate={{ scale: hoveredProduct === product.id ? 1.02 : 1 }}
                                                        transition={{ duration: 0.2 }}
                                                    >
                                                        <Typography variant="caption" sx={{
                                                            color: '#C6A43F',
                                                            letterSpacing: '2px',
                                                            fontWeight: 500,
                                                            display: 'block',
                                                            mb: 1
                                                        }}>
                                                            {product.category?.toUpperCase()} · {product.metalPurity}
                                                        </Typography>

                                                        <Typography variant="h6" sx={{
                                                            fontFamily: 'Cormorant Garamond',
                                                            fontWeight: 600,
                                                            fontSize: '1.2rem',
                                                            mb: 1,
                                                            color: '#0A0F1E'
                                                        }}>
                                                            {product.name}
                                                        </Typography>

                                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                            <Rating
                                                                value={product.rating || 4.5}
                                                                precision={0.5}
                                                                size="small"
                                                                readOnly
                                                                sx={{
                                                                    '& .MuiRating-iconFilled': {
                                                                        color: '#C6A43F',
                                                                    }
                                                                }}
                                                            />
                                                            <Typography variant="caption" sx={{ ml: 1, color: '#666' }}>
                                                                ({product.reviews || 0})
                                                            </Typography>
                                                        </Box>

                                                        <Typography variant="h6" sx={{
                                                            color: '#0A0F1E',
                                                            fontWeight: 700,
                                                            fontSize: '1.4rem',
                                                            fontFamily: 'Cormorant Garamond'
                                                        }}>
                                                            ₹{product.finalPrice?.toLocaleString()}
                                                        </Typography>

                                                        {product.type !== 'fixed' && (
                                                            <motion.div
                                                                initial={{ opacity: 0 }}
                                                                animate={{ opacity: 1 }}
                                                                transition={{ delay: 0.2 }}
                                                            >
                                                                <Typography variant="caption" sx={{
                                                                    color: '#C6A43F',
                                                                    display: 'block',
                                                                    mt: 1,
                                                                    fontStyle: 'italic'
                                                                }}>
                                                                    ✧ Dynamic pricing based on daily metal rate
                                                                </Typography>
                                                            </motion.div>
                                                        )}
                                                    </motion.div>
                                                </CardContent>
                                            </StyledCard>
                                        </motion.div>
                                    </Grid>
                                ))}
                            </AnimatePresence>
                        </Grid>

                        {/* No Results Animation */}
                        {displayedProducts.length === 0 && (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.5 }}
                                style={{ textAlign: 'center', py: 8 }}
                            >
                                <motion.div
                                    animate={{
                                        rotate: [0, 360],
                                        scale: [1, 1.1, 1]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "linear"
                                    }}
                                >
                                    <DiamondIcon sx={{ fontSize: 80, color: 'rgba(198, 164, 63, 0.3)', mb: 2 }} />
                                </motion.div>
                                <Typography variant="h5" gutterBottom sx={{ fontFamily: 'Cormorant Garamond', fontWeight: 600 }}>
                                    No products found
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Try adjusting your filters or search query
                                </Typography>
                            </motion.div>
                        )}

                        {/* Animated Pagination */}
                        {pageCount > 1 && (
                            <Fade in={true} timeout={1000}>
                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                                    <Pagination
                                        count={pageCount}
                                        page={page}
                                        onChange={(e, value) => {
                                            setPage(value);
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        color="primary"
                                        sx={{
                                            '& .MuiPaginationItem-root': {
                                                fontSize: '0.9rem',
                                                '&.Mui-selected': {
                                                    bgcolor: '#C6A43F',
                                                    color: 'white',
                                                    '&:hover': {
                                                        bgcolor: '#B3923A',
                                                    }
                                                },
                                                '&:hover': {
                                                    bgcolor: 'rgba(198, 164, 63, 0.1)',
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            </Fade>
                        )}
                    </Grid>
                </Grid>
            </Container>

            {/* Success Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                TransitionComponent={Zoom}
            >
                <Alert
                    icon={snackbar.severity === 'success' ? <CheckCircleIcon /> : <FavoriteIcon />}
                    severity={snackbar.severity}
                    sx={{
                        width: '100%',
                        backgroundColor: snackbar.severity === 'success' ? '#0A0F1E' : '#C6A43F',
                        color: snackbar.severity === 'success' ? '#C6A43F' : '#0A0F1E',
                        '& .MuiAlert-icon': {
                            color: snackbar.severity === 'success' ? '#C6A43F' : '#0A0F1E',
                        },
                        borderRadius: '50px',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
                        fontWeight: 600
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Shop;